import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { changePassword } from '../utils/apiUtils'; 
import { ROUTE_LOGIN } from '../constants'; 
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { getUserReadableError } from '../utils/utils';
import { validateField } from '../utils/validationUtils';
import './ChangePassword.css';

const ChangePassword = ({ 
  changePasswordFn = changePassword, 
  loginRoute = ROUTE_LOGIN 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordError = validateField({ isPassword: true }, formData.newPassword, 'New Password');
    if (passwordError) {
      showErrorMessage(passwordError);
      return;
    }

    if (formData.newPassword !== formData.confirmNewPassword) {
      showErrorMessage('New password and Confirm new password do not match.');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      showErrorMessage('New password cannot be the same as the current password.');
      return;
    }

    setLoading(true);
    try {
      await changePasswordFn({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmNewPassword: formData.confirmNewPassword
      });

      showSuccessMessage('Password changed successfully. Please login again.');
      navigate(loginRoute); 
    } catch (error) {
      console.error('Change password failed:', error);
      showErrorMessage(getUserReadableError(error) ?? 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <h2 className="title">Change Password</h2>
      
      <form onSubmit={(e) => handleSubmit(e)}>
        
        <div className="input-group">
          <label htmlFor="currentPassword">Current Password</label>
          <div className="password-input-wrapper">
            <input 
              type={showCurrent ? "text" : "password"} 
              id="currentPassword" 
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              autoComplete="current-password" 
              required
            />
            <button 
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowCurrent(!showCurrent)}
              tabIndex="-1"
              title={showCurrent ? "Hide password" : "Show password"}
            >
              {showCurrent ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </div>
        
        <div className="input-group">
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-wrapper">
            <input 
              type={showNew ? "text" : "password"} 
              id="newPassword" 
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              autoComplete="new-password" 
              required
            />
            <button 
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowNew(!showNew)}
              tabIndex="-1"
              title={showNew ? "Hide password" : "Show password"}
            >
              {showNew ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="confirmNewPassword">Confirm New Password</label>
          <div className="password-input-wrapper">
            <input 
              type={showConfirm ? "text" : "password"} 
              id="confirmNewPassword" 
              value={formData.confirmNewPassword}
              onChange={(e) => setFormData({ ...formData, confirmNewPassword: e.target.value })}
              autoComplete="new-password" 
              required
            />
            <button 
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirm(!showConfirm)}
              tabIndex="-1"
              title={showConfirm ? "Hide password" : "Show password"}
            >
              {showConfirm ? <EyeOff /> : <Eye />}
            </button>
          </div>
        </div>

        <div className="change-password-actions">
          <button 
            type="submit" 
            className="button primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>

          <button 
            type="button" 
            className="button secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        </div>

      </form>
    </div>
  );
};

export default ChangePassword;